<script setup lang="ts">
import type { ListboxItemEmits, ListboxItemProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { reactiveOmit, useCurrentElement } from '@vueuse/core';
import { ListboxItem, useForwardPropsEmits, useId } from 'reka-ui';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { cn } from '@/lib/utils';
import { useCommand, useCommandGroup } from '.';

const props = defineProps<ListboxItemProps & { class?: HTMLAttributes['class'] }>();
const emits = defineEmits<ListboxItemEmits>();

const delegatedProps = reactiveOmit(props, 'class');

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const id = useId();
const { filterState, allItems, allGroups, shouldFilter } = useCommand();
const groupContext = useCommandGroup();

function clearSearch() {
    filterState.search = '';
}

const isRender = computed(() => {
    if (!filterState.search || !shouldFilter.value) {
        return true;
    } else {
        const filteredCurrentItem = filterState.filtered.items.get(id);
        // If the filtered items is undefined means not in the all times map yet
        // Do the first render to add into the map
        if (filteredCurrentItem === undefined) {
            return true;
        }

        // Check with filter
        return filteredCurrentItem > 0;
    }
});

const itemRef = ref();
const currentElement = useCurrentElement(itemRef);
onMounted(() => {
    if (!(currentElement.value instanceof HTMLElement)) return;

    // textValue to perform filter
    allItems.value.set(id, currentElement.value.textContent ?? props?.value!.toString());

    const groupId = groupContext?.id;
    if (groupId) {
        if (!allGroups.value.has(groupId)) {
            allGroups.value.set(groupId, new Set([id]));
        } else {
            allGroups.value.get(groupId)?.add(id);
        }
    }
});
onUnmounted(() => {
    allItems.value.delete(id);
});
</script>

<template>
    <ListboxItem
        v-if="isRender"
        v-bind="forwarded"
        :id="id"
        ref="itemRef"
        :class="
            cn(
                'px-xs py-sm data-[highlighted]:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm text-sm outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-(--surface-highlight) [&_svg]:size-4 [&_svg]:shrink-0',
                'text-base', // atNorth overrides, TODO: put into a wrapper component
                props.class,
            )
        "
        @select="clearSearch"
    >
        <slot />
    </ListboxItem>
</template>
